import { FC, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { add } from 'date-fns';

import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { AsyncDataLoader } from '@hh.ru/magritte-ui-date-picker/AsyncDataLoader';
import { AsyncDataStore } from '@hh.ru/magritte-ui-date-picker/AsyncDataStore';
import { IntervalDayPicker } from '@hh.ru/magritte-ui-date-picker/DayPicker';
import { Header } from '@hh.ru/magritte-ui-date-picker/Header';
import { InfiniteScroll } from '@hh.ru/magritte-ui-date-picker/InfiniteScroll';
import { IntervalMonthPicker } from '@hh.ru/magritte-ui-date-picker/MonthPicker';
import { Weekdays } from '@hh.ru/magritte-ui-date-picker/Weekdays';
import { WithArrowNavigationHOC } from '@hh.ru/magritte-ui-date-picker/WithArrowNavigationHOC';
import { IntervalYearPicker } from '@hh.ru/magritte-ui-date-picker/YearPicker';
import { DateType, DragState, IntervalDatePickerProps } from '@hh.ru/magritte-ui-date-picker/types';
import { useDatePickerControls } from '@hh.ru/magritte-ui-date-picker/useDatePickerControls';
import { useDisplayDate } from '@hh.ru/magritte-ui-date-picker/utils';
import { Divider } from '@hh.ru/magritte-ui-divider';

import styles from './date-picker.less';

const SingleLayoutIntervalDatePickerRenderFunc = forwardRef<HTMLDivElement, Omit<IntervalDatePickerProps, 'layout'>>(
    (
        {
            // настройки функциональности
            disabledDates,
            disabledDatesFunc,
            displayDate: _displayDate,
            enabledCalendars,
            minDate,
            maxDate,
            selectionStart,
            selectionEnd,
            // колбеки
            onDisplayDateChange,
            onStartSelect,
            onEndSelect,
            // настройки внешнего вида
            activeHeader,
            alignHeader,
            headerMonthFormat,
            locale,
            showAdjacentDays = true,
            showControls,
            showDivider = true,
            showHeader = true,
            showHeaderDate,
            showWeekdays = true,
        },
        ref
    ) => {
        const displayDate = useDisplayDate(_displayDate, selectionStart);
        const [hoveredDate, setHoveredDate] = useState<DateType | null>(null);
        const [dragState, setDragState] = useState<DragState | null>(null);
        const { calendar, switchCalendar, zoomIntoMonth, zoomIntoYear, goToNextPeriod, goToPrevPeriod } =
            useDatePickerControls(enabledCalendars, displayDate, onDisplayDateChange);

        return (
            <div className={styles.layout} ref={ref}>
                <AsyncDataStore disabledDatesFunc={disabledDatesFunc}>
                    <AsyncDataLoader>
                        {showHeader && (
                            <Header
                                calendar={calendar}
                                displayDate={displayDate}
                                activeHeader={activeHeader}
                                alignHeader={alignHeader}
                                goToNextPeriod={goToNextPeriod}
                                goToPrevPeriod={goToPrevPeriod}
                                headerMonthFormat={headerMonthFormat}
                                locale={locale}
                                showControls={showControls}
                                showHeaderDate={showHeaderDate}
                                switchCalendar={switchCalendar}
                            />
                        )}
                        {showWeekdays && calendar === 'day' && <Weekdays locale={locale} />}
                        {showDivider && (showHeader || showWeekdays) && <Divider />}
                        {calendar === 'day' && (
                            <IntervalDayPicker
                                disabledDates={disabledDates}
                                displayDate={displayDate}
                                selectionStart={selectionStart}
                                selectionEnd={selectionEnd}
                                hoveredDate={hoveredDate}
                                dragState={dragState}
                                onDateHover={setHoveredDate}
                                onDateDrag={setDragState}
                                onStartSelect={onStartSelect}
                                onEndSelect={onEndSelect}
                                showAdjacentDays={showAdjacentDays}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        )}
                        {calendar === 'month' && (
                            <IntervalMonthPicker
                                locale={locale}
                                displayDate={displayDate}
                                selectionStart={selectionStart}
                                selectionEnd={selectionEnd}
                                onStartSelect={zoomIntoMonth}
                                onEndSelect={zoomIntoMonth}
                            />
                        )}
                        {calendar === 'year' && (
                            <IntervalYearPicker
                                displayDate={displayDate}
                                selectionStart={selectionStart}
                                selectionEnd={selectionEnd}
                                onStartSelect={zoomIntoYear}
                                onEndSelect={zoomIntoYear}
                            />
                        )}
                    </AsyncDataLoader>
                </AsyncDataStore>
            </div>
        );
    }
);

SingleLayoutIntervalDatePickerRenderFunc.displayName = 'SingleLayoutIntervalDatePickerRenderFunc';

const SingleLayoutIntervalDatePicker = WithArrowNavigationHOC(SingleLayoutIntervalDatePickerRenderFunc);

const MobileMultiLayoutIntervalDatePicker: FC<Omit<IntervalDatePickerProps, 'layout'>> = ({
    // настройки функциональности
    disabledDates,
    disabledDatesFunc,
    displayDate: _displayDate,
    selectionStart,
    selectionEnd,
    minDate,
    maxDate,
    // колбеки
    onStartSelect,
    onEndSelect,
    // настройки внешнего вида
    headerMonthFormat,
    locale,
    showDivider = true,
    showHeader = true,
    showWeekdays = true,
}) => {
    const displayDate = useDisplayDate(_displayDate, selectionStart);
    const makeDate = useCallback((index: number) => add(displayDate, { months: index }), [displayDate]);
    const stickyRef = useRef<HTMLDivElement | null>(null);
    const [visibleDate, setVisibleDate] = useState(displayDate);

    const renderDate = useCallback(
        (date: DateType) => {
            return (
                <AsyncDataLoader>
                    <Header
                        calendar="day"
                        displayDate={date}
                        activeHeader={false}
                        alignHeader="left"
                        headerMonthFormat={headerMonthFormat}
                        locale={locale}
                        showControls={false}
                    />
                    <IntervalDayPicker
                        disabledDates={disabledDates}
                        displayDate={date}
                        selectionStart={selectionStart}
                        selectionEnd={selectionEnd}
                        onStartSelect={onStartSelect}
                        onEndSelect={onEndSelect}
                        showAdjacentDays={false}
                        minDate={minDate}
                        maxDate={maxDate}
                    />
                </AsyncDataLoader>
            );
        },
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
        [disabledDates, headerMonthFormat, locale, selectionStart, selectionEnd, onStartSelect, onEndSelect]
    );

    const onVisibleItemChange = useCallback(
        (date: DateType) => {
            setVisibleDate(date);
        },
        [setVisibleDate]
    );

    useEffect(() => {
        if (stickyRef.current !== null) {
            stickyRef.current.style.setProperty('--sticky-top', `${Math.floor(stickyRef.current.offsetTop)}px`);
        }
    }, []);

    return (
        <div className={styles.layoutVertical}>
            <div className={styles.stickyWrapper} ref={stickyRef}>
                {showHeader && (
                    <Header
                        calendar="month"
                        displayDate={visibleDate}
                        activeHeader={false}
                        alignHeader="left"
                        locale={locale}
                        showControls={false}
                    />
                )}
                {showWeekdays && <Weekdays locale={locale} />}
                {showDivider && (showHeader || showWeekdays) && <Divider />}
            </div>
            <AsyncDataStore disabledDatesFunc={disabledDatesFunc} parallelLoadersLimit={1}>
                <InfiniteScroll
                    initialRenderedItems={5}
                    invisibleThreshold={300}
                    visibleThreshold={100}
                    makeItemFunc={makeDate}
                    renderItemFunc={renderDate}
                    onVisibleItemChange={onVisibleItemChange}
                />
            </AsyncDataStore>
        </div>
    );
};

const DesktopMultiLayoutIntervalDatePickerRenderFunc = forwardRef<
    HTMLDivElement,
    Omit<IntervalDatePickerProps, 'layout'>
>(
    (
        {
            // настройки функциональности
            disabledDates,
            disabledDatesFunc,
            displayDate,
            enabledCalendars,
            selectionStart,
            selectionEnd,
            minDate,
            maxDate,
            // колбеки
            onDisplayDateChange,
            onStartSelect,
            onEndSelect,
            // настройки внешнего вида
            headerMonthFormat,
            locale,
            showControls,
            showDivider = true,
            showHeader = true,
            showHeaderDate,
            showWeekdays = true,
        },
        ref
    ) => {
        const leftDisplayDate = useDisplayDate(displayDate, selectionStart);
        const rightDisplayDate = useMemo(() => add(leftDisplayDate, { months: 1 }), [leftDisplayDate]);
        const [hoveredDate, setHoveredDate] = useState<DateType | null>(null);
        const [dragState, setDragState] = useState<DragState | null>(null);
        const { goToNextPeriod, goToPrevPeriod } = useDatePickerControls(
            enabledCalendars,
            leftDisplayDate,
            onDisplayDateChange
        );

        return (
            <div className={styles.layoutHorizontal} ref={ref}>
                <AsyncDataStore disabledDatesFunc={disabledDatesFunc}>
                    <AsyncDataLoader>
                        {showHeader && (
                            <Header
                                calendar="day"
                                displayDate={leftDisplayDate}
                                activeHeader={false}
                                alignHeader="center"
                                goToPrevPeriod={goToPrevPeriod}
                                headerMonthFormat={headerMonthFormat}
                                locale={locale}
                                showControls={showControls}
                                showHeaderDate={showHeaderDate}
                                showNextControl={false}
                            />
                        )}
                        {showWeekdays && <Weekdays locale={locale} />}
                        {showDivider && (showHeader || showWeekdays) && <Divider />}
                        <IntervalDayPicker
                            disabledDates={disabledDates}
                            displayDate={leftDisplayDate}
                            selectionStart={selectionStart}
                            selectionEnd={selectionEnd}
                            hoveredDate={hoveredDate}
                            dragState={dragState}
                            onStartSelect={onStartSelect}
                            onEndSelect={onEndSelect}
                            onDateHover={setHoveredDate}
                            onDateDrag={setDragState}
                            showAdjacentDays={false}
                            minDate={minDate}
                            maxDate={maxDate}
                        />
                    </AsyncDataLoader>
                    <AsyncDataLoader>
                        {showHeader && (
                            <Header
                                calendar="day"
                                displayDate={rightDisplayDate}
                                activeHeader={false}
                                alignHeader="center"
                                goToNextPeriod={goToNextPeriod}
                                headerMonthFormat={headerMonthFormat}
                                locale={locale}
                                showControls={showControls}
                                showHeaderDate={showHeaderDate}
                                showPrevControl={false}
                            />
                        )}
                        {showWeekdays && <Weekdays locale={locale} />}
                        {showDivider && (showHeader || showWeekdays) && <Divider />}
                        <IntervalDayPicker
                            disabledDates={disabledDates}
                            displayDate={rightDisplayDate}
                            selectionStart={selectionStart}
                            selectionEnd={selectionEnd}
                            hoveredDate={hoveredDate}
                            dragState={dragState}
                            onStartSelect={onStartSelect}
                            onEndSelect={onEndSelect}
                            onDateHover={setHoveredDate}
                            onDateDrag={setDragState}
                            showAdjacentDays={false}
                            minDate={minDate}
                            maxDate={maxDate}
                        />
                    </AsyncDataLoader>
                </AsyncDataStore>
            </div>
        );
    }
);

DesktopMultiLayoutIntervalDatePickerRenderFunc.displayName = 'DesktopMultiLayoutIntervalDatePickerRenderFunc';

const DesktopMultiLayoutIntervalDatePicker = WithArrowNavigationHOC(DesktopMultiLayoutIntervalDatePickerRenderFunc);

export const IntervalDatePicker: FC<IntervalDatePickerProps> = ({ layout = 'single', ...props }) => {
    const { isMobile } = useBreakpoint();

    if (layout === 'single') {
        return <SingleLayoutIntervalDatePicker {...props} />;
    }

    if (layout === 'multi' && isMobile) {
        return <MobileMultiLayoutIntervalDatePicker {...props} />;
    }

    if (layout === 'multi' && !isMobile) {
        return <DesktopMultiLayoutIntervalDatePicker {...props} />;
    }

    return null;
};

export const UncontrolledIntervalDatePicker: FC<IntervalDatePickerProps> = ({
    displayDate: initialDisplayDate,
    selectionStart: initialSelectionStart,
    selectionEnd: initialSelectionEnd,
    onDisplayDateChange,
    onStartSelect,
    onEndSelect,
    ...props
}) => {
    const [displayDate, setDisplayDate] = useState<DateType>(useDisplayDate(initialDisplayDate, initialSelectionStart));
    const [selectionStart, setSelectionStart] = useState<DateType | null>(initialSelectionStart ?? null);
    const [selectionEnd, setSelectionEnd] = useState<DateType | null>(initialSelectionEnd ?? null);

    const handleDisplayDateChange = useCallback(
        (value: DateType) => {
            onDisplayDateChange?.(value);
            setDisplayDate(value);
        },
        [onDisplayDateChange, setDisplayDate]
    );

    const handleStartSelect = useCallback(
        (value: DateType | null) => {
            onStartSelect?.(value);
            setSelectionStart(value);
        },
        [onStartSelect, setSelectionStart]
    );

    const handleEndSelect = useCallback(
        (value: DateType | null) => {
            onEndSelect?.(value);
            setSelectionEnd(value);
        },
        [onEndSelect, setSelectionEnd]
    );

    return (
        <IntervalDatePicker
            {...props}
            displayDate={displayDate}
            onDisplayDateChange={handleDisplayDateChange}
            selectionStart={selectionStart}
            selectionEnd={selectionEnd}
            onStartSelect={handleStartSelect}
            onEndSelect={handleEndSelect}
        />
    );
};
